import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Typography } from '../../ui-kit';
import styles from './tg-provider.module.scss';
import { useAuth } from '@/store/useAuth';
import { debounce } from 'lodash';
import { profilesApi } from '@/api';
import { IDevice, UAParser } from 'ua-parser-js';
import { routesApp } from '@/constans/routes';
import { callYaMetrika } from '@/utils/call-ya-metrika';
import { useToast } from '@/store/useToast';
import { ActivatedProfileIcon, ErrorNotificationIcon } from '@/assets/icons';
import { useNavHistory } from '@/store/useNavHistory';
import { Trans, useTranslation } from 'react-i18next';

export const TgProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const navigate = useNavigate();
    const showToast = useToast((state) => state.show);

    const handleBack = useCallback(() => navigate(-1), [navigate]);

    const userData = useAuth((state) => state.getUser());
    const setUserData = useAuth((state) => state.setProfile);

    useEffect(() => {
        /*
         * show always back button
         * if (WebApp.BackButton.isVisible) {
         *     WebApp.onEvent('backButtonClicked', handleBack);
         * }
         */
        WebApp.onEvent('backButtonClicked', handleBack);
        return () => {
            WebApp.offEvent('backButtonClicked', handleBack);
        };
    }, [handleBack]);

    useEffect(() => {
        const isActive = userData?.active;
        if (
            isActive !== null &&
            isActive !== undefined
        ) setOverlayOpened(isActive === false);
    }, [userData]);

    useEffect(() => {
        const parser = new UAParser();
        setDevice(parser.getDevice());
    }, []);

    const location = useLocation();

    const [isOverlayOpened, setOverlayOpened] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [device, setDevice] = useState<IDevice | null>(null);

    const PATH_SEPARATOR = '/';

    const splitPathToChunks = (path: string) => path.split(PATH_SEPARATOR);

    const joinPathChunks = (chunks: string[]) => chunks.join(PATH_SEPARATOR);

    useEffect(() => {
        const chunksCount = splitPathToChunks(location.pathname).filter((e: string) => e.length).length;
        if (chunksCount >= 2) WebApp.BackButton.show();
        else WebApp.BackButton.hide();

        // override default stack
        const PATH_PARAM_PREFIX = ':';
        const chunks = location.pathname.split(PATH_SEPARATOR);

        if (window.history.length === 1) {
            if (
                location
                    .pathname
                    .startsWith(
                        joinPathChunks(
                            splitPathToChunks(
                                routesApp.chat
                            )
                                .filter(
                                    (e) => !e.startsWith(PATH_PARAM_PREFIX)
                                )
                        )
                    )
            ) {
                navigate(routesApp.messages, {
                    replace: true,
                });
                navigate(joinPathChunks(chunks));
            }
        }
    }, [location]);

    const { t } = useTranslation();

    const activateProfileDebounce = debounce(async () => {
        if (isLoading) return;
        setIsLoading(true);
        callYaMetrika('return_to_search');
        try {
            const res = await profilesApi.activate();
            if (res.status !== 200) return showActivateError();
            // TODO: use svg file instead
            showToast({
                icon: <ActivatedProfileIcon className={styles.activedProfileIcon} />,
                msg: t('you_are_searching_again')
            });
            setOverlayOpened(false);
            setUserData({
                ...userData,
                active: true,
            });
        } catch (e) {
            showActivateError();
        } finally {
            setIsLoading(false);
        }
    }, 750);

    const showActivateError = () =>
        showToast({
            icon: <ErrorNotificationIcon />,
            msg: t('error_try_again')
        });

    const onActivateProfileClick = () => activateProfileDebounce();

    const { setPrevRoute } =
        useNavHistory(state => state);

    useEffect(() => {
        // Save current location as "previous" before updating
        return () =>
            setPrevRoute(location.pathname);
    }, [location.pathname]);

    return (
        <div>
            <Outlet />
            {location.pathname === 'profile' || !isOverlayOpened ? (
                // placeholder
                <div />
            ) : (
                <div
                    className={[
                        styles.overlay,
                        ...(device?.type === 'mobile' || device?.type === 'tablet' ? [styles.overlayMobile] : []),
                    ].join(' ')}>
                    <Typography
                        center
                        fontWeight="semibold"
                        fontFamily="SFProText">
                        <Trans
                            i18nKey={'return_to_search'}
                            components={[
                                <a href="#" onClick={onActivateProfileClick} />
                            ]} />
                    </Typography>
                </div>
            )}
        </div>
    );
};
